
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import React, { Fragment, useContext, useEffect, useState } from "react";
import propTypes from "prop-types";
import { api, DeviceConfig, DeviceDefinition, DeviceTypes, RequestStatus } from "../../../logic/api";
import { SessionContext } from "../../../utility/useSession";
import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import flex from "../../../styles/flex";
import { formatCamel } from "../../../utility/utils";
import SwitchField from "../../../components/editable/EditableField/fields/SwitchField";
import TextInputField from "../../../components/editable/EditableField/fields/TextInputField";
import NumberInputField from "../../../components/editable/EditableField/fields/NumberInputField";
import AlertDialog from "../../../components/notifications/alertDialog/AlertDialog";
import NotchedCard from "../../../components/cards/NotchedCard/NotchedCard";
import { Trans, useTranslation } from "react-i18next";

interface IFeature {
    enabled?: boolean;
    featureType?: string;
    features?: Record<string, IFeature>;
}

interface IFeatureOption {
    value: unknown;
    input: string;
}

const DeviceConfigDialog = (props: {
    show: boolean,
    deviceConfigId?: number | undefined,
    onClose: (didUpdate: boolean) => void
}) => {

    const getDefinition = api.hooks.deviceManager.getDefinition();
    const getDeviceConfig = api.hooks.deviceManager.getById();
    const saveDeviceConfig = api.hooks.deviceManager.saveOrCreate();

    const session = useContext(SessionContext);
    const { t } = useTranslation();

    const [showConfigSaveAlert, setShowConfigSaveAlert] = useState(false);

    const [definition, setDefinition] = useState<DeviceDefinition | null>(null);

    const emptyDeviceConfig: DeviceConfig = {
        active: true,
        id: props.deviceConfigId,
        data: "{}",
        name: t("mobile.newConfig"),
        deviceType: DeviceTypes.mobile,
    };

    const [deviceConfig, setDeviceConfig] = useState<DeviceConfig>(emptyDeviceConfig);

    let deviceConfigDataObject: Record<string, unknown> = {};

    if (deviceConfig.data.length > 0) {
        try {
            deviceConfigDataObject = JSON.parse(deviceConfig.data);
        } catch (e) {
            console.warn(e);
        }
    }

    const propSets: Record<string, unknown> = {};
    const updatePropertySet = (keys: string[], prop: string, defaultValue: unknown) => {
        const path = [...keys, prop].join(".");
        propSets[path] = defaultValue;
    }

    const setDeviceProperty = (keys: string[], prop: string, defaultValue: unknown) => {
        const path = [...keys, prop].join(".");

        deviceConfigDataObject[path] = defaultValue;

        setDeviceConfig({
            ...deviceConfig,
            data: JSON.stringify(deviceConfigDataObject)
        });
    }

    const getDeviceProperty = (keys: string[], prop: string, defaultValue: unknown) => {
        const path = [...keys, prop].join(".");
        return deviceConfigDataObject[path] ?? defaultValue;
    }



    const saveConfig = (closeAfterSave: boolean) => {
        const deviceCfg = { ...deviceConfig };
        deviceCfg.data = JSON.stringify({ ...propSets, ...JSON.parse(deviceCfg.data) });
        saveDeviceConfig.send(session.current.context, deviceCfg, (result) => {
            if (result != null && closeAfterSave == true) {
                props.onClose(true);
            } else if (result != null) {
                setDeviceConfig(result);
                setShowConfigSaveAlert(true);
            }
        });
    }


    let rootFeature: IFeature | null = null;
    if (definition?.data != null) {
        try {
            rootFeature = JSON.parse(definition?.data);
        } catch (e) {
            console.warn(e);
        }
    }

    useEffect(() => {
        if (props.show == true && rootFeature == null) {
            getDefinition.send(session.current.context, {}, (result) => {
                setDefinition(result);
            });
        }
    }, [props.show]);

    useEffect(() => {
        if (props.deviceConfigId == undefined) {
            setDeviceConfig(emptyDeviceConfig);
        } else {
            getDeviceConfig.send(session.current.context, { id: props.deviceConfigId }, (result) => {
                const cfg = result ?? emptyDeviceConfig;
                setDeviceConfig(cfg);
            });
        }
    }, [props.deviceConfigId]);



    const renderFeature = (feature: IFeature | null, featureKey: string | null, parentKey: string | null, keys: string[]) => {
        if (feature == null) return <Fragment />;
        const exclude = ["featureType", "enabled", "features"];
        const featureEnabled = featureKey == null || getDeviceProperty(keys, "enabled", feature.enabled == true || feature.enabled == null) == true;

        const features = featureEnabled ? feature.features ?? {} : {};
        const featureOptionKeys = featureEnabled ? Object.keys(feature).filter((f) => exclude.indexOf(f) == -1) : [];
        const subFeatureKeys = Object.keys(features);

        if (feature.enabled != null && featureKey != null) {
            updatePropertySet(keys, "enabled", featureEnabled);
        }


        return <div>
            {
                (featureKey != null) &&
                (
                    <div style={flex.flexRows}>
                        {
                            (featureKey) &&
                            (
                                <div style={flex.flexAuto}>
                                    <SwitchField
                                        readOnly={feature.enabled == null}
                                        showAsCheck={true}
                                        value={featureEnabled}
                                        onChange={(checked) => setDeviceProperty(keys, "enabled", checked == true)}
                                    />
                                </div>
                            )
                        }
                        <div style={flex.flexFill}>
                            <div style={parentKey == null ? { paddingTop: 8 } : { paddingTop: 10 }}>
                                <b style={parentKey == null ? { color: "blue", fontSize: 16, } : {}}>
                                    {formatCamel(featureKey)}
                                </b>
                            </div>
                        </div>
                    </div>
                )
            }
            {
                featureOptionKeys.map((featureOptionKey, f) => {
                    const option: IFeatureOption = (feature as Record<string, IFeatureOption>)[featureOptionKey];
                    const labelText = formatCamel(featureOptionKey);

                    let input: React.ReactElement<unknown>;
                    const value = getDeviceProperty(keys, featureOptionKey, option.value);

                    updatePropertySet(keys, featureOptionKey, option.value);

                    if (option.input == "text") {
                        input = <TextInputField
                            label={labelText}
                            value={value as string}
                            onChange={(value) => setDeviceProperty(keys, featureOptionKey, value)}
                        />
                    } else if (option.input == "text_multi") {
                        input = <TextInputField
                            maxWidth={400}
                            multiLine={true}
                            label={labelText}
                            value={value as string}
                            onChange={(value) => setDeviceProperty(keys, featureOptionKey, value)}
                        />
                    } else if (option.input == "integer") {
                        input = <NumberInputField
                            label={labelText}
                            value={value as number}
                            onChange={(value) => setDeviceProperty(keys, featureOptionKey, value)}
                        />
                    } else {
                        input =
                            <Trans i18nKey="mobile.unknownInputType"
                                values={{ label: labelText, option: option.input }}
                                components={{
                                    bold: <strong />,
                                    redText: <span style={{ color: "red" }} />
                                }}
                            />;
                    }
                    return <div key={f} style={{ marginLeft: 50, marginBottom: 10 }}>
                        {input}
                    </div>
                })
            }
            {subFeatureKeys.map((subFeatureKey, f) => {
                const subFeature = features[subFeatureKey];
                const subKeys = [...keys, subFeatureKey];
                return <Fragment key={f}>
                    <div style={{ marginLeft: featureKey == null ? 0 : 50, marginBottom: 5 }}>
                        {renderFeature(subFeature, subFeatureKey, featureKey, subKeys)}
                    </div>
                </Fragment>;
            })}

        </div>
    }

    return <Fragment>
        <ProgressDialog status={getDefinition.status} />
        <ErrorDialog err={getDefinition.err} />


        <ErrorDialog err={getDeviceConfig.err} />
        <ProgressDialog status={getDeviceConfig.status} />

        <Dialog fullWidth={true} maxWidth="lg" open={props.show && rootFeature != null && (getDeviceConfig.status == RequestStatus.loaded || props.deviceConfigId == undefined)}>
            <DialogTitle style={{ textAlign: "center" }}>
                {t("mobile.mobileConfigEditor")}
            </DialogTitle>
            <DialogContent style={{ minHeight: 170, overflow: "hidden" }}>
                <ProgressDialog status={saveDeviceConfig.status} />
                <ErrorDialog err={saveDeviceConfig.err} />

                <AlertDialog
                    title={t("mobile.deviceConfigSaved")}
                    message={t("mobile.configUpdated")}
                    show={showConfigSaveAlert}
                    onClose={() => {
                        setShowConfigSaveAlert(false);
                    }}
                />

                <NotchedCard label={t("mobile.configSettings")}>
                    <div style={{ height: 20 }} />
                    <TextInputField
                        maxLength={50}
                        label={t("mobile.configName")}
                        value={deviceConfig.name}
                        onChange={(value) => (setDeviceConfig({ ...deviceConfig, name: value }))}
                    />

                    <SwitchField
                        label={t("mobile.active")}
                        value={deviceConfig.active == true}
                        onChange={(value) => (setDeviceConfig({ ...deviceConfig, active: value }))}
                    />

                </NotchedCard>

            </DialogContent>
            <DialogContent>

                {renderFeature(rootFeature, null, null, [])}

            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        props.onClose(false);
                    }}
                >
                    {t("general.cancel")}
                </Button>
                <Button
                    onClick={() => saveConfig(false)}>
                    <strong>{t("general.saveChanges")}</strong>
                </Button>
                <Button
                    onClick={() => saveConfig(true)}>
                    <strong>{t("general.saveAndClose")}</strong>
                </Button>
            </DialogActions>

        </Dialog>


    </Fragment>
}

DeviceConfigDialog.propTypes = {
    show: propTypes.bool.isRequired,
    deviceConfigId: propTypes.number,
    onClose: propTypes.func.isRequired,
}

export default DeviceConfigDialog;